import { Box, Flex, Spinner, useDisclosure, useToast } from '@chakra-ui/react';
import { QueryWrapperNext } from '@diamond/shared/data-access';
import { Account, ProductKeyword } from '@diamond/shared/types';
import { Button, Modal, showToast, Text } from '@diamond/shared/ui';
import { apiInstance, SessionRecoveryError } from '@diamond/shared/utils';
import {
  useAuthentication,
  useAuthStore,
  useNotificationCount,
  usePwaStore,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { setCookie } from 'cookies-next';
import { sort } from 'fast-sort';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import shallow from 'zustand/shallow';

import { getAxiosNetworkFailureMessage } from '../pwa/network-error-message';
import {
  BOTTOM_NAV_HEIGHT,
  shouldShowBuyerBottomNav,
} from './bottom-nav/bottom-nav-config';
import { BuyerBottomNav } from './bottom-nav/BuyerBottomNav';
import Footer from './Footer';
import {
  BUYER_LAYOUT_HORIZONTAL_PADDING,
  PageContainer,
} from './layout-constants';
import { OptionShipTo } from './OptionShipTo';

interface BuyerLayoutProps {
  children: ReactNode;
  containerClassname?: string;
}

const NavHeaderNoSSR = dynamic(() => import('./NavHeader'), {
  ssr: false,
});

export function BuyerLayout({
  children,
  containerClassname,
}: BuyerLayoutProps) {
  const router = useRouter();
  const access_token = useAuthStore((state) => state.access_token);
  const isPwaOffline = usePwaStore((state) => state.isPwaOffline);

  const [
    selectedShipTo,
    selectedShipToId,
    availableShipTo,
    setAvailableShipTo,
    setSelectedShipToId,
    setSelectedShipTo,
    setSelectedShipToDc,
  ] = useShipToStore(
    (s) => [
      s.selectedShipTo,
      s.selectedShipToId,
      s.availableShipTo,
      s.setAvailableShipTo,
      s.setSelectedShipToId,
      s.setSelectedShipTo,
      s.setSelectedShipToDc,
    ],
    shallow
  );

  const { onClose } = useDisclosure();
  const { logout, ensureSession, expireSession, sessionRecovery } =
    useAuthentication();
  const queryClient = useQueryClient();
  const { data: notification } = useNotificationCount();
  const query = QueryWrapperNext('/product/keyword', access_token, {
    enabled: !!selectedShipToId,
  });
  const [stateChanged, setStateChanged] = useState<boolean>(false);
  const toast = useToast();

  const [showModalShipTo, setShowModalShipTo] = useState(
    selectedShipToId === ''
  );
  const [isFetchingShipTo, setIsFetchingShipTo] = useState(
    availableShipTo.length === 0
  );
  const [shipToLoadError, setShipToLoadError] = useState(false);

  const setUserRole = useAuthStore((s) => s.setUserRole);

  const { data: productKeyword } = query.useQueryGet<ProductKeyword>(
    'ProductKeyword',
    { warehouse: selectedShipToId }
  );

  const productKeywordList = Array.isArray(productKeyword)
    ? productKeyword
    : [];

  const suggestedProduct = productKeywordList.map((val) => {
    return {
      indexId: val.index,
      label: val.name,
      url: `${val.url}&cat1=&page=1`,
    };
  });

  const suggestedProductSorted = sort(suggestedProduct).asc(
    (a: { indexId: number; label: string; url: string }) => a.indexId
  );

  const refetchUserData = async () => {
    setIsFetchingShipTo(true);
    setShipToLoadError(false);
    let endedSession = false;
    try {
      if (!(await ensureSession())) return;
      const token = useAuthStore.getState().access_token;
      const { data: userData } = await apiInstance(token).get<Account>(
        '/account/me'
      );
      const shipToState = useShipToStore.getState();
      const currentList = shipToState.availableShipTo;
      const currentSelectedId = shipToState.selectedShipToId;
      const currentRole = useAuthStore.getState().userRole;
      const isBootstrapping = currentList.length === 0;
      const nextShipTos = userData.ship_tos;
      const selectedStillExists = nextShipTos.some(
        (shipTo) => shipTo.id === currentSelectedId
      );

      if (currentRole !== userData.role) {
        if (!isBootstrapping) {
          setStateChanged(true);
        }
        setUserRole(userData.role);
        setCookie('buyer_active_role', userData.role);
      }

      if (currentList.length !== nextShipTos.length) {
        if (!isBootstrapping) {
          setStateChanged(true);
        }
        setAvailableShipTo(nextShipTos);
        if (selectedStillExists) {
          const nextSelected = nextShipTos.find(
            (shipTo) => shipTo.id === currentSelectedId
          );
          if (nextSelected) {
            setSelectedShipTo(nextSelected);
          }
          return setShowModalShipTo(false);
        }
        setSelectedShipToId('');
        setCookie('selected_shipto_id', '');
        setCookie('selected_shipto_dc', '');
        return setShowModalShipTo(true);
      }

      if (currentSelectedId === '') {
        return setShowModalShipTo(true);
      }

      return setShowModalShipTo(false);
    } catch (error) {
      if (error instanceof SessionRecoveryError) {
        setShipToLoadError(true);
        return;
      }
      if (error instanceof AxiosError && error.response?.status === 401) {
        endedSession = true;
        await expireSession();
        return;
      }
      const idToast = 'error-conn';
      setShipToLoadError(true);
      if (error instanceof AxiosError && error.code === 'ERR_NETWORK') {
        if (!isPwaOffline && !toast.isActive(idToast)) {
          showToast(
            toast,
            'error',
            getAxiosNetworkFailureMessage(navigator.onLine),
            idToast,
            'top',
            30000
          );
        }
      }
    } finally {
      if (!endedSession) {
        setIsFetchingShipTo(false);
      }
    }
  };

  const latestRefetch = useRef(refetchUserData);
  latestRefetch.current = refetchUserData;
  useEffect(() => {
    const resume = () => {
      if (document.visibilityState === 'visible') void latestRefetch.current();
    };
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, []);

  const handleModalShipTo = () => {
    setShowModalShipTo(false);
    queryClient.clear();
    onClose();
  };

  useMemo(() => {
    if (availableShipTo.length === 1) {
      setShowModalShipTo(false);
      setSelectedShipToId(availableShipTo[0].id);
      setSelectedShipToDc(availableShipTo[0].distribution_channel);
      setSelectedShipTo(availableShipTo[0]);
      setAvailableShipTo(availableShipTo);

      setCookie('selected_shipto_id', availableShipTo[0].id);
      setCookie('selected_shipto_dc', availableShipTo[0].distribution_channel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableShipTo]);

  useEffect(() => {
    const persist = useShipToStore.persist;
    const run = () => {
      void refetchUserData();
    };
    if (persist?.hasHydrated?.()) {
      run();
      return;
    }
    if (persist?.onFinishHydration) {
      return persist.onFinishHydration(run);
    }
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderShipToOptions = () => {
    if (isFetchingShipTo && availableShipTo.length === 0) {
      return (
        <Flex justify="center" py={8} data-testid="shipto-loading">
          <Spinner />
        </Flex>
      );
    }
    if (shipToLoadError && availableShipTo.length === 0) {
      return (
        <Flex direction="column" align="center" py={6} gap={3}>
          <Text>Gagal memuat daftar Ship To.</Text>
          <Button onClick={() => refetchUserData()}>Coba Lagi</Button>
        </Flex>
      );
    }
    return (
      <OptionShipTo shipTos={availableShipTo} onSubmit={handleModalShipTo} />
    );
  };

  // Delay in milliseconds
  useInterval(() => refetchUserData(), isPwaOffline ? null : 1000 * 30);

  const showBottomNav = shouldShowBuyerBottomNav(router.pathname);
  const notificationCount = notification?.count || 0;

  if (sessionRecovery !== 'idle') {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="60vh"
        gap={4}
        role="status"
      >
        {sessionRecovery === 'temporary-failure' ? (
          <>
            <Text>Gagal memulihkan sesi. Silakan coba lagi.</Text>
            <Button onClick={() => void refetchUserData()}>Coba lagi</Button>
            <Button variant="link" onClick={() => void expireSession()}>
              Masuk kembali
            </Button>
          </>
        ) : (
          <>
            <Spinner />
            <Text>
              {sessionRecovery === 'redirecting'
                ? 'Mengalihkan ke halaman masuk…'
                : 'Memulihkan sesi…'}
            </Text>
          </>
        )}
      </Flex>
    );
  }

  return (
    <>
      <NavHeaderNoSSR
        suggestedProducts={suggestedProductSorted}
        onLogout={() => logout(access_token)}
        userLogin={selectedShipTo?.search_term_shipto}
        shipTo={availableShipTo}
        selectedShipToId={selectedShipToId}
        setSelectedShipToId={setSelectedShipToId}
        showBackButton={router.pathname !== '/'}
        notificationCount={notificationCount}
      />
      <Box
        as={PageContainer}
        className={containerClassname}
        px={BUYER_LAYOUT_HORIZONTAL_PADDING}
        pt={3}
        pb={
          showBottomNav
            ? {
                base: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
                md: 0,
              }
            : undefined
        }
        transition="padding .3s"
      >
        {children}
        <Footer />
      </Box>
      {showBottomNav ? (
        <BuyerBottomNav notificationCount={notificationCount} />
      ) : null}

      <Modal
        title="Pilih Ship To"
        name="select-shipto"
        isOpen={showModalShipTo}
        scrollBehavior="inside"
        onClose={onClose}
        onSubmit={onClose}
        hideAction
        hideCancel
        hideClose
        size="xl"
      >
        {renderShipToOptions()}
      </Modal>

      <Modal
        isCentered
        title="Pemberitahuan"
        name="state-changed"
        isOpen={stateChanged}
        onClose={onClose}
        onSubmit={() => {
          setStateChanged(false);
          queryClient.clear();
        }}
        actionButtonPosition="center"
        labelSubmit="Lanjutkan"
        hideCancel
        hideClose
        size="md"
      >
        <div>Terdapat perubahan Role atau ShipTo pada akun Anda!</div>
      </Modal>
    </>
  );
}
